// src/features/procedures/hooks/useProcedures.js
import { useState, useCallback } from 'react';
import { proceduresAPI } from '../services/procedures';
import { useApi } from '../../../hooks/api/useApi';

export function useProcedures() {
  const [procedures, setProcedures] = useState([]);
  const [currentProcedure, setCurrentProcedure] = useState(null);
  const [currentExecution, setCurrentExecution] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  const fetchProcedures = useCallback(async (includeInactive = false) => {
    const result = await execute(() => proceduresAPI.listProcedures(includeInactive));
    if (result.success) {
      setProcedures(result.data.data || []);
    }
    return result;
  }, [execute]);

  const fetchProcedureById = useCallback(async (procedureId) => {
    const result = await execute(() => proceduresAPI.getProcedureById(procedureId));
    if (result.success) {
      setCurrentProcedure(result.data.data);
    }
    return result;
  }, [execute]);

  const createProcedure = useCallback(async (data) => {
    const result = await execute(() => proceduresAPI.createProcedure(data));
    if (result.success) {
      await fetchProcedures();
    }
    return result;
  }, [execute, fetchProcedures]);

  const updateProcedure = useCallback(async (procedureId, data) => {
    const result = await execute(() => proceduresAPI.updateProcedure(procedureId, data));
    if (result.success) {
      setProcedures(prev => prev.map(p =>
        p.procedure_id === procedureId ? { ...p, ...result.data.data } : p
      ));
      if (currentProcedure?.procedure_id === procedureId) {
        setCurrentProcedure({ ...currentProcedure, ...result.data.data });
      }
    }
    return result;
  }, [execute, currentProcedure]);

  const deleteProcedure = useCallback(async (procedureId) => {
    const result = await execute(() => proceduresAPI.deleteProcedure(procedureId));
    if (result.success) {
      setProcedures(prev => prev.filter(p => p.procedure_id !== procedureId));
    }
    return result;
  }, [execute]);

  const addProcedureStep = useCallback(async (procedureId, stepData) => {
    const result = await execute(() => proceduresAPI.addProcedureStep(procedureId, stepData));
    if (result.success) {
      await fetchProcedureById(procedureId);
    }
    return result;
  }, [execute, fetchProcedureById]);

  const linkProcedureToTask = useCallback(async (taskId, procedureId) => {
    const result = await execute(() => 
      proceduresAPI.linkProcedureToTask(taskId, { procedure_id: procedureId })
    );
    return result;
  }, [execute]);

  const startProcedureExecution = useCallback(async (taskId) => {
    const result = await execute(() =>
      proceduresAPI.startProcedureExecution(taskId, {})
    );
    if (result.success) {
      setCurrentExecution(result.data.data);
    }
    return result;
  }, [execute]);

  const fetchProcedureExecution = useCallback(async (executionId) => {
    const result = await execute(() => proceduresAPI.getProcedureExecution(executionId));
    if (result.success) {
      setCurrentExecution(result.data.data);
    }
    return result;
  }, [execute]);

  const submitStepResponse = useCallback(async (executionId, responseData) => {
    const result = await execute(() => 
      proceduresAPI.submitStepResponse(executionId, responseData)
    );
    if (result.success) {
      setCurrentExecution(result.data.data);
    }
    return result;
  }, [execute]);

  const completeProcedureExecution = useCallback(async (executionId) => {
    const result = await execute(() => 
      proceduresAPI.completeProcedureExecution(executionId)
    );
    if (result.success) {
      setCurrentExecution(result.data.data);
    }
    return result;
  }, [execute]);

  const cancelProcedureExecution = useCallback(async (executionId) => {
    const result = await execute(() => 
      proceduresAPI.cancelProcedureExecution(executionId)
    );
    if (result.success) {
      setCurrentExecution(result.data.data);
    }
    return result;
  }, [execute]);

  return {
    procedures,
    currentProcedure,
    currentExecution,
    loading,
    error,
    clearError,
    fetchProcedures,
    fetchProcedureById,
    createProcedure,
    updateProcedure,
    deleteProcedure,
    addProcedureStep,
    linkProcedureToTask,
    startProcedureExecution,
    fetchProcedureExecution,
    submitStepResponse,
    completeProcedureExecution,
    cancelProcedureExecution,
  };
}